import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { usePostHog } from 'posthog-react-native'
import { rootDb } from '../global/Firebase/firebase'
import { snapshotExists } from '../global/Firebase/types'
import { buildProvenanceProperties } from '../utils/buildProvenance'
import {
  bundleGateProcess,
  evaluateBundleFloor,
  nextBundleLock,
  persistableFloorConfig,
  resolveRunningBundleInstant,
  shouldReportBundleEvaluation,
  updateBackoffActive,
  type BundleFloorConfig,
  type BundleFloorEvaluation,
  type BundleFloorOutcome,
  type BundleIdentity,
} from '../utils/bundleIdentity'
import {
  probeBackoffStorage,
  readBackoffRecord,
  readLastFloorConfig,
  writeLastFloorConfig,
} from '../utils/bundleLockStore'
import { withTimeout } from '../utils/withTimeout'

/**
 * ATL-1452 — THE BUNDLE GATE, mounted ONCE per process in Routes, above the signed-in / signed-out
 * branch. (It used to live in AppStack's version check, which remounts on every sign-out/sign-in and
 * auth flicker; most of what review found wrong was machinery for surviving those remounts.)
 *
 * ONE ORDERED STREAM decides everything: the `config/app` listener's snapshots.
 *   • The launch is the only moment a lock may be TAKEN (never later: the cashier may be mid-sale,
 *     and the lockout restarts the app), and only a SERVER snapshot may take it. A CACHED copy can
 *     only OPEN the launch early — Firestore's cache (native) or the floor fields the gate itself
 *     stored from the last server snapshot (every platform; web has no Firestore cache): one under
 *     which this launch would not lock (no hard floor this bundle is below, or that floor backed off)
 *     decides it at once, so a till that starts offline or on a slow network does not wait for a floor
 *     nobody set. (A floor no cached copy has seen yet is enforced from the launch after it arrives.)
 *     Otherwise the app waits for the first server snapshot for at most LAUNCH_SNAPSHOT_TIMEOUT_MS;
 *     if none arrives (offline — no OTA could be fetched anyway), the launch decides nothing: fail open.
 *   • A lock is only taken where it cannot loop: the back-off must be READABLE (an unreadable one is
 *     not taken for "none") and WRITABLE here (probeBackoffStorage) — otherwise the restart would be
 *     vetoed and the lockout would only flash. Storage that cannot answer in time fails open.
 *   • Once a restart is ISSUED (bundleGateProcess.restartIssued) the lock is final for the process.
 *   • Every later server snapshot can only RELEASE the lock (the kill switch: floor removed, set soft,
 *     no longer below it) or report the verdict (the soft rollout's telemetry). Snapshots are handled
 *     strictly one after another, so a slower evaluation can never overtake a newer one.
 *   • The lock ENDS with its one update attempt (BundleUpdateScreen → `release`).
 */

export const LAUNCH_SNAPSHOT_TIMEOUT_MS = 10 * 1000
/** A launch never waits on storage longer than this; storage that cannot answer in time never locks. */
const STORAGE_TIMEOUT_MS = 2000
/** After a listener error, re-subscribe after this long (it is the kill switch's only path). */
const LISTENER_RETRY_MS = 30 * 1000

/**
 * The floor as telemetry names it. Native and web floors are DIFFERENT instants (an OTA publish vs a
 * web export stamp), so every event says which field it is.
 */
export const bundleFloorProperties = (
  floor: string | null
): { bundle_floor: string | null; bundle_floor_field: string } => ({
  bundle_floor: floor,
  bundle_floor_field: Platform.OS === 'web' ? 'minWebBundleBuiltAt' : 'minBundlePublishedAt',
})

type Provenance = ReturnType<typeof buildProvenanceProperties>

/**
 * The RUNNING bundle's identity, from the SAME fields every PostHog event carries and the gate reads
 * (buildProvenanceProperties) — so the lockout and the cutover gate cannot source it differently.
 */
export const runningBundleIdentity = (
  provenance: Provenance = buildProvenanceProperties()
): BundleIdentity => {
  const p = provenance as Record<string, unknown>
  return resolveRunningBundleInstant({
    platform: String(p.platform),
    updateId: typeof p.update_id === 'string' ? p.update_id : null,
    isEmbeddedLaunch: typeof p.update_is_embedded === 'boolean' ? p.update_is_embedded : null,
    webBuildAt: typeof p.web_build_at === 'string' ? p.web_build_at : null,
  })
}

const evaluateCurrentBundle = (data: BundleFloorConfig | null): BundleFloorEvaluation => {
  const provenance = buildProvenanceProperties() as Record<string, unknown>
  return evaluateBundleFloor({
    identity: runningBundleIdentity(provenance as Provenance),
    platform: Platform.OS,
    runtimeVersion: typeof provenance.runtime_version === 'string' ? provenance.runtime_version : null,
    config: data,
  })
}

/**
 * Why a lock the SERVER lifted was lifted — named from the verdict, never a catch-all. (`below-hard`
 * never releases a lock, so it has no reason.)
 */
const releaseReasonFor = (outcome: Exclude<BundleFloorOutcome, 'below-hard'>): string =>
  ({
    'no-floor': 'floor-removed',
    'below-soft': 'floor-soft',
    ok: 'floor-met',
    'invalid-floor': 'floor-invalid',
    'other-runtime': 'other-runtime',
    'unknown-identity': 'unknown-identity',
  })[outcome]

interface ConfigSnapshot {
  exists?: boolean | (() => boolean)
  data?: () => unknown
  metadata?: { fromCache?: boolean }
}

export interface BundleGate {
  /** False until the launch has been decided (at most LAUNCH_SNAPSHOT_TIMEOUT_MS). */
  launchDecided: boolean
  locked: boolean
  /** The floor the lock holds for (named back in `release`). */
  floor: string | null
  /** An operator-provided lockout message from config/app, if any. */
  message: string | null
  /** The lock's one attempt ended without a restart: the lock ends here. */
  release: (floor: string | null, reason: string) => void
}

export function useBundleGate(): BundleGate {
  const postHog = usePostHog()
  const postHogRef = useRef(postHog)
  postHogRef.current = postHog
  const [state, setState] = useState(() => ({
    launchDecided: bundleGateProcess.launched, // a remount decides nothing new
    locked: bundleGateProcess.locked,
    floor: bundleGateProcess.lockedFloor,
    message: null as string | null,
  }))
  /** Bumped to re-subscribe the listener after an error. */
  const [subscription, setSubscription] = useState(0)

  useEffect(() => {
    let alive = true
    let launchOpen = !bundleGateProcess.launched
    bundleGateProcess.launched = true
    let lastKey: string | null = null
    let queue: Promise<void> = Promise.resolve()
    let retry: ReturnType<typeof setTimeout> | null = null
    const capture = (event: string, props: Record<string, string | number | boolean | null>): void => {
      try {
        postHogRef.current?.capture?.(event, props)
      } catch {
        /* telemetry never decides the lock */
      }
    }
    const launchTimer = launchOpen
      ? setTimeout(() => {
          if (!launchOpen) return
          launchOpen = false // no server read in time: the launch decides nothing (fail open)
          capture('bundle_gate_evaluated', {
            outcome: 'no-server-read',
            trigger: 'launch',
            enforced: false,
            platform: Platform.OS,
          })
          if (alive) setState((s) => ({ ...s, launchDecided: true }))
        }, LAUNCH_SNAPSHOT_TIMEOUT_MS)
      : null

    /** Why a LAUNCH that would newly lock this floor must not (null: it may lock). */
    const launchSuppression = async (floor: string): Promise<string | null> => {
      const read = await withTimeout(readBackoffRecord(), STORAGE_TIMEOUT_MS, () =>
        new Error('back-off read timed out')
      ).catch(() => ({ readable: false as const }))
      // Unreadable is NOT "no back-off": a 'restarted' record may be there, and locking would loop.
      if (!read.readable) return 'backoff-unreadable'
      // Skipped while this floor is backed off (UPDATE_BACKOFF_MS)…
      const backoff = updateBackoffActive(read.value, floor, Date.now())
      if (backoff) return `recent-${backoff}`
      // …and wherever the back-off could not be written: the restart would be vetoed.
      const writable = await withTimeout(probeBackoffStorage(), STORAGE_TIMEOUT_MS, () =>
        new Error('storage probe timed out')
      ).catch(() => false)
      return writable ? null : 'no-storage'
    }

    const handle = async (data: BundleFloorConfig | null, fromCache: boolean): Promise<void> => {
      // A cached copy decides nothing once the launch is decided.
      if (fromCache && !launchOpen) return
      const bundle = evaluateCurrentBundle(data)
      const atLaunch = launchOpen
      // A SERVER snapshot decides the launch now (the timer must not fire while it is evaluated).
      if (!fromCache && launchOpen) {
        launchOpen = false
        if (launchTimer) clearTimeout(launchTimer)
      }
      const suppressed =
        atLaunch && bundle.outcome === 'below-hard' && bundle.floor
          ? await launchSuppression(bundle.floor)
          : null
      if (fromCache) {
        // Only the server may lock: a cached copy under which this launch WOULD lock waits for it.
        // (Re-checked after the await: the launch timer may have decided the launch meanwhile.)
        if (!launchOpen || (bundle.outcome === 'below-hard' && !suppressed)) return
        launchOpen = false
        if (launchTimer) clearTimeout(launchTimer)
      } else {
        // What the NEXT launch may open on (never awaited: it decides nothing here).
        void writeLastFloorConfig(persistableFloorConfig(data))
      }
      const trigger = atLaunch ? 'launch' : 'foreground'
      if (!alive) return
      const wasLocked = bundleGateProcess.locked
      const heldFloor = bundleGateProcess.lockedFloor
      const locked =
        // An ISSUED restart cannot be called back: the lock is final (releasing would restart mid-sale).
        wasLocked && bundleGateProcess.restartIssued
          ? true
          : suppressed
            ? wasLocked
            : nextBundleLock({
                locked: wasLocked,
                outcome: bundle.outcome,
                trigger,
              })
      // A re-pointed floor re-points the lock (a new attempt) — except under an issued restart.
      const floor = locked ? (bundleGateProcess.restartIssued ? heldFloor : bundle.floor) : null
      bundleGateProcess.locked = locked
      bundleGateProcess.lockedFloor = floor
      const rawMessage = (data as { bundleLockoutMessage?: unknown } | null)?.bundleLockoutMessage
      setState({
        launchDecided: true,
        locked,
        floor,
        message: typeof rawMessage === 'string' && rawMessage ? rawMessage : null,
      })
      if (shouldReportBundleEvaluation(bundle, locked)) {
        capture('bundle_gate_evaluated', {
          outcome: bundle.outcome,
          source: bundle.source,
          running_at: bundle.runningAt,
          ...bundleFloorProperties(bundle.floor),
          trigger: trigger === 'launch' ? 'launch' : 'listener',
          from_cache: fromCache,
          // Whether a below-hard verdict actually LOCKED this till (never after launch, never while
          // backed off) — so a dashboard never reads "below-hard" as enforcement that did not happen.
          enforced: bundle.outcome === 'below-hard' ? locked : null,
          suppressed,
          platform: Platform.OS,
        })
      }
      // A lock the SERVER lifted — reported for a lockout someone SAW, naming the floor it HELD.
      if (wasLocked && !locked && bundleGateProcess.lockReported && bundle.outcome !== 'below-hard') {
        capture('bundle_lockout_released', {
          reason: releaseReasonFor(bundle.outcome),
          ...bundleFloorProperties(heldFloor),
        })
      }
    }

    const dataOf = (snap: ConfigSnapshot): BundleFloorConfig | null =>
      snapshotExists(snap) ? ((snap.data?.() ?? null) as BundleFloorConfig | null) : null
    // Strictly one after another: a slower evaluation can never overtake a newer snapshot.
    const enqueue = (data: BundleFloorConfig | null, fromCache: boolean): void => {
      queue = queue
        .then(() => handle(data, fromCache))
        .catch((error: unknown) => console.warn('bundle gate: snapshot not handled', error))
    }

    // The floor fields the last server snapshot carried — the cached copy every platform has.
    if (launchOpen) {
      void withTimeout(readLastFloorConfig(), STORAGE_TIMEOUT_MS, () => new Error('config read timed out'))
        .catch(() => ({ readable: false as const }))
        .then((read) => {
          if (!alive || !launchOpen) return
          // Storage that cannot be read cannot hold a back-off either: this launch can never lock, so
          // it opens now. Readable but never written (a first launch): wait for the server.
          if (!read.readable) enqueue(null, true)
          else if (read.value && typeof read.value === 'object') enqueue(read.value as BundleFloorConfig, true)
        })
    }

    let unsubscribe: () => void = () => {}
    try {
      unsubscribe = rootDb
        .collection('config')
        .doc('app')
        // includeMetadataChanges: with offline persistence the first snapshot comes from the cache,
        // and the server CONFIRMING identical data is a metadata-only change — without this option
        // the launch would never see a server snapshot unless config/app was edited.
        .onSnapshot(
          { includeMetadataChanges: true },
          (snap: ConfigSnapshot) => {
            if (snap?.metadata?.fromCache === true) {
              if (launchOpen) enqueue(dataOf(snap), true)
              return
            }
            let key: string
            try {
              key = JSON.stringify(snapshotExists(snap) ? snap.data?.() : null)
            } catch {
              key = `unserialisable:${Date.now()}`
            }
            // A metadata-only repeat with the SAME data decides nothing new (a reconnect).
            if (key === lastKey) return
            lastKey = key
            enqueue(dataOf(snap), false)
          },
          (error: unknown) => {
            // The listener is the kill switch's only path to a locked till: re-subscribe.
            console.warn('bundle gate: config/app listener stopped; re-subscribing', error)
            retry = setTimeout(() => setSubscription((n) => n + 1), LISTENER_RETRY_MS)
          }
        )
    } catch (error) {
      console.warn('bundle gate: config/app listener could not start; retrying', error)
      retry = setTimeout(() => setSubscription((n) => n + 1), LISTENER_RETRY_MS)
    }
    return () => {
      alive = false
      if (launchTimer) clearTimeout(launchTimer)
      if (retry) clearTimeout(retry)
      unsubscribe()
    }
  }, [subscription])

  const release = useCallback((floor: string | null, reason: string): void => {
    // An issued restart is never released under (the attempt only ends after one that FAILED, which
    // clears the flag first).
    if (bundleGateProcess.restartIssued) return
    bundleGateProcess.locked = false
    bundleGateProcess.lockedFloor = null
    // (Nothing more to remember: a lock is only ever TAKEN at the launch, once per process, so a
    // released floor cannot be re-taken — the next LAUNCH re-evaluates, after any back-off.)
    setState((s) => ({ ...s, locked: false, floor: null }))
    try {
      postHogRef.current?.capture?.('bundle_lockout_released', {
        reason,
        ...bundleFloorProperties(floor),
      })
    } catch {
      /* telemetry never decides the lock */
    }
  }, [])

  return { ...state, release }
}
